import { FC, useState } from "react"
import {
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material"
import { attachmentModule, wasmModule } from "@/gluelayer"

export const TopPanel: FC = () => {
  const [selectedValue, setSelectedValue] = useState<number>(0)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)

  // 处理单选项切换并同步到 wasm
  const handleSelectionChange = (value: string) => {
    setSelectedValue(Number(value))
    wasmModule.ulabwinIns.ShowSingleAttachmentSetting(value)
  }

  // 打开确认弹窗并缓存待执行操作
  const handleConfirmDialog = (action: () => void) => {
    setPendingAction(() => action)
    setConfirmDialogOpen(true)
  }

  // 确认弹窗后执行已缓存操作
  const handleDialogConfirm = () => {
    setConfirmDialogOpen(false)
    if (pendingAction) {
      pendingAction()
      setPendingAction(null)
    }
  }

  // 取消弹窗并清理待执行操作
  const handleDialogCancel = () => {
    setConfirmDialogOpen(false)
    setPendingAction(null)
  }

  // 使用 Reva 默认参数
  const handleUseRevaSettings = () => {
    handleConfirmDialog(() => {
      attachmentModule.UseDefaultsSettings("0")
    })
  }

  // 使用 uLab 默认参数
  const handleUseUlabDefaultSettings = () => {
    handleConfirmDialog(() => {
      attachmentModule.UseDefaultsSettings("1")
    })
  }

  const options = [
    { label: "Distal", value: 0 },
    { label: "Buccal", value: 1 },
    { label: "Extrusion", value: 2 },
    { label: "Torque+", value: 3 },
    { label: "Tipping+", value: 4 },
    { label: "Rotation+", value: 5 },
    { label: "Mesial", value: 6 },
    { label: "Lingual", value: 7 },
    { label: "Intrusion", value: 8 },
    { label: "Torque-", value: 9 },
    { label: "Tipping-", value: 10 },
    { label: "Rotation-", value: 11 },
  ]

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        display: "flex",
        gap: 4,
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "repeat(6, 1fr)",
          gridTemplateRows: "repeat(2, 1fr)",
          gap: "12px 8px",
          alignItems: "center",
          px: 3,
          py: 2,
          backgroundColor: "#ffffff",
        }}
      >
        <RadioGroup
          row
          value={selectedValue}
          onChange={(e) => handleSelectionChange(e.target.value)}
          sx={{
            gridTemplateColumns: "repeat(6, 1fr)",
            gridTemplateRows: "repeat(2, 1fr)",
            gap: "12px 8px",
            display: "grid",
          }}
        >
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={
                <Radio
                  size="small"
                  sx={{
                    py: 0,
                    "& .MuiSvgIcon-root": {
                      fontSize: "18px",
                    },
                  }}
                />
              }
              label={option.label}
              sx={{
                m: 0,
                justifyContent: "flex-start",
                "& .MuiFormControlLabel-label": {
                  fontSize: "13px",
                  fontWeight: 500,
                },
              }}
            />
          ))}
        </RadioGroup>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 1,
        }}
      >
        <Button
          variant="contained"
          onClick={handleUseRevaSettings}
          sx={{
            backgroundColor: "#42A5F5",
            color: "#fff",
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 500,
            px: 3,
            py: 1.5,
            width: "auto",
            minWidth: "160px",
            "&:hover": {
              backgroundColor: "#1E88E5",
            },
          }}
        >
          Use Reva settings
        </Button>
        <Button
          variant="contained"
          onClick={handleUseUlabDefaultSettings}
          sx={{
            backgroundColor: "#42A5F5",
            color: "#fff",
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 500,
            px: 3,
            py: 1.5,
            minWidth: "160px",
            "&:hover": {
              backgroundColor: "#1E88E5",
            },
          }}
        >
          Use uLab default settings
        </Button>
      </Box>

      <Typography
        sx={{
          position: "absolute",
          left: 0,
          bottom: -70,
          fontSize: "12px",
          lineHeight: 1.4,
          color: "#f44336",
          whiteSpace: "pre-line",
        }}
      >
        {`An attachment and pressure point needs to be at least 1mm from a slit,
button cutout and gingival margin.
Pressure point needs to be 2mm from the occlusal edge.
The recommended depth (D) of all attachments shouldn't exceed 1.5mm.`}
      </Typography>

      <Dialog open={confirmDialogOpen}>
        <DialogTitle sx={{ padding: "16px 24px" }}></DialogTitle>
        <DialogContent sx={{ paddingBottom: "12px", paddingTop: "10px" }}>
          It will restore the single attachment placement settings back to uLab
          default settings, proceed?
        </DialogContent>
        <DialogActions sx={{ padding: "12px 24px" }}>
          <Button
            variant="outlined"
            onClick={handleDialogCancel}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 400,
              px: 3,
              py: 1,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDialogConfirm}
            sx={{
              backgroundColor: "#42A5F5",
              color: "#fff",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              px: 4,
              py: 1,
              "&:hover": {
                backgroundColor: "#1E88E5",
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
